import { isOverdue } from '@/lib/format'
import type { PostEtapa, ProductionStage, TaskStatus } from '@/types/database'
import type { TaskWithRelations } from '@/types/domain'

/** Mantém o status legado em sincronia com a etapa (KPIs/calendário usam status). */
export const ETAPA_TO_STATUS: Record<PostEtapa, TaskStatus> = {
  ideia: 'entrada',
  roteiro: 'pronto_para_produzir',
  gravacao: 'em_producao',
  edicao: 'em_producao',
  aprovacao: 'aguardando_aprovacao',
  agendado: 'agendado',
  publicado: 'publicado',
}

export type StationState = 'done' | 'curr' | 'late' | 'empty' | 'pending'
export type StationKey = 'roteiro' | 'gravacao' | 'postagem'

export interface Station {
  key: StationKey
  label: string
  state: StationState
  stage: ProductionStage | null
  date: string | null
}

const ORDER: StationKey[] = ['roteiro', 'gravacao', 'postagem']
const VERB: Record<StationKey, string> = {
  roteiro: 'ROTEIRO',
  gravacao: 'GRAVAÇÃO',
  postagem: 'POSTAGEM',
}

/** Deriva as 3 estações a partir das colunas de estágio (pipeline estruturado). */
export function deriveStations(task: TaskWithRelations): Station[] {
  const overdue =
    isOverdue(task.data_postagem ?? task.agreed_due_date ?? task.requested_due_date) &&
    task.status !== 'publicado'

  const stageOf: Record<StationKey, ProductionStage | null> = {
    roteiro: task.stage_roteiro,
    gravacao: task.stage_gravacao,
    postagem: task.stage_postagem,
  }
  const dateOf: Record<StationKey, string | null> = {
    roteiro: task.data_roteiro,
    gravacao: task.data_gravacao,
    postagem: task.data_postagem,
  }

  // estação atual = primeira que não está concluída (e não é vazia)
  const currentKey = ORDER.find((k) => stageOf[k] && stageOf[k] !== 'concluido') ?? null

  return ORDER.map((key) => {
    const stage = stageOf[key]
    let state: StationState
    if (!stage) state = 'empty'
    else if (stage === 'concluido') state = 'done'
    else if (key === currentKey) state = overdue ? 'late' : 'curr'
    else state = 'pending'
    return { key, label: VERB[key], state, stage, date: dateOf[key] }
  })
}

export function hasProductionStations(task: TaskWithRelations): boolean {
  return task.is_content
}

/** Sugere a etapa (status do Kanban) a partir dos 3 estágios. */
export function deriveEtapa(
  r: ProductionStage | null,
  g: ProductionStage | null,
  p: ProductionStage | null,
): TaskStatus {
  if (p === 'concluido') return 'publicado'
  if (g === 'concluido') return 'agendado'
  if (g === 'em_andamento') return 'em_producao'
  if (r === 'concluido') return 'pronto_para_produzir'
  if (r === 'em_andamento') return 'em_producao'
  return 'entrada'
}
